import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { tokens } from '../../theme-next';
import { isPdf } from '../../utils';
import { getPdfThumbnail } from '../../utils/pdfThumbnail';
import { DocLinksPanel } from './DocLinksPanel';
import { MAX_VIEWER_ZOOM, minViewerZoom, viewerKeyAction, ViewerKeyAction } from './inlineViewerKeys';

export interface ViewerFile {
  url: string;
  /** Type humanisé (ex: "Carte grise", "Cerfa cession"). */
  label: string;
  /**
   * Type effectif de la pièce (slot routé personne × face inclus) — sert au
   * tri/groupage de `DocLinksPanel`. Absent = pièce non analysée/non reconnue.
   */
  docType?: string;
  /** Confiance OCR en pourcent, optionnelle (affichée comme métadonnée). */
  confidence?: number;
  /**
   * Pièce présente au dossier mais SANS extraction OCR : elle n'a été vue par
   * aucun moteur d'analyse (cross-checks, audit Opus). On l'affiche quand même
   * — sinon le relecteur ignore jusqu'à son existence — mais en le disant
   * explicitement, pour ne pas laisser croire qu'elle a été vérifiée.
   * (Retour François 2026-07-20 EQ-598-TE puis 2026-07-22 GY-530-NL.)
   */
  unanalyzed?: boolean;
}

interface InlineFileViewerProps {
  files: ViewerFile[];
  currentIndex: number;
  onChange: (index: number) => void;
  /** Décalage sticky depuis le haut : sous le bandeau figé de la review. */
  stickyTop?: number;
  /**
   * Retire la pièce affichée du dossier (SKIP-355). Absent = pas de bouton.
   * L'appelant rafraîchit la liste ; le visualiseur recale son index seul.
   */
  onDelete?: (file: ViewerFile) => Promise<void>;
}

/**
 * Visualiseur de pièces inline (pas de modale). Affiche le fichier courant
 * (PDF en iframe, image en <img>), avec navigation prev/next + miniatures
 * cliquables en bas. Pattern dérivé de FileCarouselDialog mais embedded
 * dans la page de review.
 */
export const InlineFileViewer = ({ files, currentIndex, onChange, stickyTop = 24, onDelete }: InlineFileViewerProps) => {
  const [deleteTarget, setDeleteTarget] = useState<ViewerFile | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const confirmDelete = async () => {
    if (!deleteTarget || !onDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(deleteTarget);
      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Impossible de retirer la pièce du dossier.');
    } finally {
      setDeleting(false);
    }
  };
  const safeIndex = Math.max(0, Math.min(currentIndex, files.length - 1));
  const current = files[safeIndex];
  const isCurrentPdf = current ? isPdf(current.url) : false;
  // Un PDF ne dézoome pas sous 100 % : l'iframe doit toujours remplir la zone
  // d'affichage (cf. minViewerZoom). Les images gardent un plancher à 50 %.
  const minZoom = minViewerZoom(isCurrentPdf);

  // Rotation et zoom purement visuels (non persistés). Reset à chaque
  // changement de fichier — sinon on garde l'angle / le zoom d'un doc
  // précédent par mégarde.
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  // Déplacement (pan) quand on est zoomé : sans ça, `scale()` zoomait toujours
  // au centre et on ne pouvait pas atteindre les bords de la pièce. On déplace
  // via translate, pilotable au glisser-déposer OU au pavé directionnel.
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{ startX: number; startY: number; baseX: number; baseY: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    setRotation(0);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [safeIndex]);
  // À 100 % (ou moins), le pan n'a pas de sens : on le remet à zéro.
  useEffect(() => {
    if (zoom <= 1) setPan({ x: 0, y: 0 });
  }, [zoom]);
  // Si le plancher remonte (image 50 % → PDF) sans changement d'index, on
  // ramène le zoom au plancher pour que la pièce remplisse toujours la zone.
  useEffect(() => {
    setZoom((z) => Math.max(minZoom, z));
  }, [minZoom]);

  // Amplitude de déplacement autorisée, proportionnelle au sur-zoom : à zoom 1
  // → 0 (verrouillé), plus on zoome plus on peut s'éloigner du centre.
  const panLimit = Math.max(0, (zoom - 1) * 480);
  const clampPan = useCallback(
    (p: { x: number; y: number }) => ({
      x: Math.max(-panLimit, Math.min(panLimit, p.x)),
      y: Math.max(-panLimit, Math.min(panLimit, p.y)),
    }),
    [panLimit],
  );
  const PAN_STEP = 120;
  // Le sens d'une flèche = direction du REGARD : « → » révèle la partie droite
  // (l'image glisse à gauche), etc.
  const panView = useCallback(
    (dirX: number, dirY: number) => setPan((p) => clampPan({ x: p.x - dirX * PAN_STEP, y: p.y - dirY * PAN_STEP })),
    [clampPan],
  );

  const onDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      dragRef.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y };
      setDragging(true);
    },
    [zoom, pan.x, pan.y],
  );
  const onDragMove = useCallback(
    (e: React.MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      setPan(clampPan({ x: d.baseX + (e.clientX - d.startX), y: d.baseY + (e.clientY - d.startY) }));
    },
    [clampPan],
  );
  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    setDragging(false);
  }, []);

  const handlePrev = useCallback(() => {
    if (files.length === 0) return;
    onChange((safeIndex - 1 + files.length) % files.length);
  }, [files.length, safeIndex, onChange]);

  const handleNext = useCallback(() => {
    if (files.length === 0) return;
    onChange((safeIndex + 1) % files.length);
  }, [files.length, safeIndex, onChange]);

  const rotateLeft = useCallback(() => setRotation((r) => (r - 90 + 360) % 360), []);
  const rotateRight = useCallback(() => setRotation((r) => (r + 90) % 360), []);

  // Zoom borné à [minZoom, 4]. Au-delà l'image déborde tellement qu'elle
  // devient illisible sans panning, et en dessous on perd la lecture des champs.
  const zoomIn = useCallback(() => setZoom((z) => Math.min(MAX_VIEWER_ZOOM, +(z + 0.25).toFixed(2))), []);
  const zoomOut = useCallback(() => setZoom((z) => Math.max(minZoom, +(z - 0.25).toFixed(2))), [minZoom]);
  const zoomReset = useCallback(() => setZoom(1), []);

  // Raccourcis clavier : ← / → navigation, R / Shift+R rotation, + / - zoom,
  // 0 reset zoom. La décision (modifieurs système, focus dans un input…) est
  // dans viewerKeyAction — cf. inlineViewerKeys.test.ts.
  useEffect(() => {
    const actions: Record<ViewerKeyAction, () => void> = {
      prev: handlePrev,
      next: handleNext,
      rotateLeft,
      rotateRight,
      zoomIn,
      zoomOut,
      zoomReset,
    };
    const handleKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const action = viewerKeyAction({
        key: e.key,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey,
        ctrlKey: e.ctrlKey,
        altKey: e.altKey,
        targetTagName: target?.tagName,
        targetIsEditable: target?.isContentEditable,
      });
      if (action) actions[action]();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handlePrev, handleNext, rotateLeft, rotateRight, zoomIn, zoomOut, zoomReset]);

  if (!current) {
    return (
      <Box
        sx={{
          backgroundColor: tokens.color.white,
          border: `1px solid ${tokens.color.border}`,
          borderRadius: `${tokens.radius.xl}px`,
          padding: '40px',
          textAlign: 'center',
          color: tokens.color.textMuted,
        }}
      >
        Aucun document attaché à cette demande.
      </Box>
    );
  }

  // État local : a-t-on échoué à charger le fichier ? Réinitialisé à
  // chaque changement de currentIndex. Sert à afficher un fallback lisible
  // au lieu d'un cadre blanc quand l'URL Firebase est expirée, vide, ou
  // qu'un CDN tombe en 404 (cas typique du Cerfa "qui ne charge pas").
  const [loadError, setLoadError] = useState<boolean>(false);
  useEffect(() => {
    setLoadError(false);
  }, [safeIndex]);

  const hasValidUrl = typeof current.url === 'string' && current.url.length > 0;
  const showFallback = !hasValidUrl || loadError;

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.white,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius.xl}px`,
        boxShadow: tokens.shadow.card,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'sticky',
        top: stickyTop,
        // Offset volontairement faible : sur un écran 13" (~740px de hauteur),
        // chaque pixel d'offset est pris à l'aperçu. On garde le top + ~86px
        // de marge basse, tout le reste va à la carte.
        height: `calc(100vh - ${stickyTop + 86}px)`,
        minHeight: 400,
        // Fenêtre de pièces redimensionnable (retour François DY-565-KJ) : le
        // relecteur peut agrandir/réduire la hauteur du visualiseur en tirant la
        // poignée en bas à droite. `resize` natif — robuste, pas de JS.
        resize: 'vertical',
        maxHeight: '94vh',
      }}
    >
      {/* ── Header : type doc + position ────────────────── */}
      <Box
        sx={{
          padding: '12px 16px',
          borderBottom: `1px solid ${tokens.color.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexShrink: 0,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: tokens.color.textMuted,
            }}
          >
            Document {safeIndex + 1} / {files.length}
          </Typography>
          <Typography
            sx={{
              fontSize: '17px',
              fontWeight: 800,
              color: tokens.color.dark,
              lineHeight: 1.2,
              marginTop: '2px',
            }}
          >
            {current.label}
            {current.unanalyzed && (
              <Typography
                component="span"
                sx={{
                  marginLeft: '8px',
                  padding: '2px 8px',
                  borderRadius: '999px',
                  fontSize: '11px',
                  fontWeight: 800,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: '#8a5a00',
                  backgroundColor: '#fff3d6',
                  border: '1px solid #f0c96b',
                  verticalAlign: 'middle',
                }}
                title="Cette pièce est au dossier mais n'a été analysée par aucun moteur (OCR, cross-checks, audit) — à contrôler visuellement."
              >
                Non analysée
              </Typography>
            )}
            {typeof current.confidence === 'number' && (
              <Typography
                component="span"
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: tokens.color.textMuted,
                  marginLeft: '8px',
                }}
              >
                · IA {current.confidence}%
              </Typography>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
          {/* Wrappers <span> autour des IconButton désactivés : MUI Tooltip
              ne peut pas écouter les events d'un bouton disabled, sinon il
              warn en console — on lui fournit un span receveur d'event. */}
          {/* Indicateur visible dès qu'on n'est pas à 100 % : sans lui, un zoom
              résiduel (pièce rétrécie ou agrandie) n'est signalé nulle part et
              passe pour un bug de mise en page. */}
          {zoom !== 1 && (
            <Typography sx={{ fontSize: '11px', fontWeight: 700, color: tokens.color.textMuted, marginRight: '2px' }}>
              {Math.round(zoom * 100)} %
            </Typography>
          )}
          <Tooltip title="Dézoomer (−)" placement="bottom" arrow>
            <span>
              <IconButton
                onClick={zoomOut}
                size="small"
                disabled={zoom <= minZoom}
                sx={{ color: tokens.color.textMuted }}
              >
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`Zoom 100 % (touche 0) — actuel : ${Math.round(zoom * 100)} %`} placement="bottom" arrow>
            <span>
              <IconButton onClick={zoomReset} size="small" disabled={zoom === 1} sx={{ color: tokens.color.textMuted }}>
                <ZoomOutMapIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoomer (+)" placement="bottom" arrow>
            <span>
              <IconButton
                onClick={zoomIn}
                size="small"
                disabled={zoom >= MAX_VIEWER_ZOOM}
                sx={{ color: tokens.color.textMuted }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Pivoter à gauche (Shift+R)" placement="bottom" arrow>
            <IconButton onClick={rotateLeft} size="small" sx={{ color: tokens.color.textMuted }}>
              <RotateLeftIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Pivoter à droite (R)" placement="bottom" arrow>
            <IconButton onClick={rotateRight} size="small" sx={{ color: tokens.color.textMuted }}>
              <RotateRightIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Ouvrir dans un nouvel onglet" placement="left" arrow>
            <IconButton
              component="a"
              href={current.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              sx={{ color: tokens.color.textMuted }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onDelete && (
            <Tooltip title="Supprimer cette pièce du dossier" placement="left" arrow>
              <IconButton
                onClick={() => {
                  setDeleteError(null);
                  setDeleteTarget(current);
                }}
                size="small"
                aria-label="Supprimer cette pièce"
                sx={{ color: tokens.color.red }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {onDelete && (
        <Dialog open={deleteTarget !== null} onClose={() => (deleting ? undefined : setDeleteTarget(null))} maxWidth="xs" fullWidth>
          <DialogTitle>Supprimer cette pièce ?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              <strong>{deleteTarget?.label}</strong> sera retirée du dossier et son fichier effacé. Cette action ne se
              défait pas.
            </Typography>
            {deleteError && (
              <Typography variant="body2" sx={{ color: tokens.color.red, mt: 1 }}>
                {deleteError}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Annuler
            </Button>
            <Button onClick={() => void confirmDelete()} color="error" variant="contained" disabled={deleting}>
              {deleting ? 'Suppression…' : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Zone d'affichage ────────────────────────────── */}
      <Box
        onMouseDown={onDragStart}
        onMouseMove={onDragMove}
        onMouseUp={onDragEnd}
        onMouseLeave={onDragEnd}
        sx={{
          flex: 1,
          position: 'relative',
          backgroundColor: tokens.color.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          // Quand zoomé, on déplace via translate (drag ou pavé directionnel) :
          // le curseur signale qu'on peut saisir l'image pour la déplacer.
          cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
        }}
      >
        {isCurrentPdf ? (
          // Astuce : pour rotation 90/270, on inverse les dimensions afin que
          // le PDF rotaté reste contenu dans la zone visible (sinon il déborde
          // ou est tronqué). Pour 0/180, on garde 100% × 100%.
          <Box
            sx={{
              width: rotation % 180 === 0 ? '100%' : '100%',
              height: rotation % 180 === 0 ? '100%' : '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${zoom})`,
              transformOrigin: 'center center',
              transition: dragging ? 'none' : 'transform 200ms ease',
              // Pour les rotations 90/270, on swappe width/height au niveau
              // du conteneur pour que l'iframe occupe toute la zone correctement.
              ...(rotation % 180 !== 0
                ? {
                    width: '100vh',
                    maxWidth: '100%',
                  }
                : {}),
            }}
          >
            <iframe
              key={current.url}
              src={current.url}
              title={current.label}
              onError={() => setLoadError(true)}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: tokens.color.white,
                // Pendant un glisser, on coupe les events de l'iframe pour que le
                // pan du parent reçoive bien le mouvement (sinon l'iframe les
                // capte et le drag ne fonctionne pas sur un PDF). Hors drag, on
                // rend l'interaction native du lecteur PDF.
                pointerEvents: dragging ? 'none' : 'auto',
              }}
            />
          </Box>
        ) : showFallback ? (
          <Box
            sx={{
              padding: '40px 24px',
              textAlign: 'center',
              maxWidth: 420,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: tokens.color.dark }}>
              Aperçu indisponible
            </Typography>
            <Typography sx={{ fontSize: '13px', color: tokens.color.textMuted }}>
              Ce fichier ({current.label}) n'a pas pu être chargé. L'URL est peut-être expirée ou le document a échoué à
              l'extraction.
            </Typography>
            {hasValidUrl && (
              <Box
                component="a"
                href={current.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  marginTop: '8px',
                  fontSize: '12px',
                  fontWeight: 700,
                  color: tokens.color.blue,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Ouvrir le lien direct ↗
              </Box>
            )}
          </Box>
        ) : (
          <img
            key={current.url}
            src={current.url}
            alt={current.label}
            draggable={false}
            onError={() => setLoadError(true)}
            style={{
              maxWidth: rotation % 180 === 0 ? '100%' : '90%',
              maxHeight: rotation % 180 === 0 ? '100%' : '90%',
              objectFit: 'contain',
              transform: `translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg) scale(${zoom})`,
              transformOrigin: 'center center',
              // Pas de transition pendant le drag, sinon le pan « traîne ».
              transition: dragging ? 'none' : 'transform 200ms ease',
            }}
          />
        )}

        {files.length > 1 && (
          <>
            <NavButton position="left" onClick={handlePrev}>
              <ArrowBackIosNewIcon fontSize="small" />
            </NavButton>
            <NavButton position="right" onClick={handleNext}>
              <ArrowForwardIosIcon fontSize="small" />
            </NavButton>
          </>
        )}

        {/* Pavé directionnel : apparaît en zoom (image OU PDF). Permet de
            naviguer dans la pièce (atteindre les bords) au lieu de rester
            bloqué au centre. Le glisser-déposer fait la même chose. */}
        {zoom > 1 && !showFallback && (
          <Box
            sx={{
              position: 'absolute',
              right: 12,
              bottom: 12,
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 30px)',
              gridTemplateRows: 'repeat(3, 30px)',
              gap: '2px',
              backgroundColor: 'rgba(28, 28, 30, 0.55)',
              borderRadius: `${tokens.radius.md}px`,
              padding: '4px',
            }}
          >
            <Box />
            <PanButton onClick={() => panView(0, -1)} label="Voir le haut">
              <KeyboardArrowUpIcon fontSize="small" />
            </PanButton>
            <Box />
            <PanButton onClick={() => panView(-1, 0)} label="Voir la gauche">
              <KeyboardArrowLeftIcon fontSize="small" />
            </PanButton>
            <Box />
            <PanButton onClick={() => panView(1, 0)} label="Voir la droite">
              <KeyboardArrowRightIcon fontSize="small" />
            </PanButton>
            <Box />
            <PanButton onClick={() => panView(0, 1)} label="Voir le bas">
              <KeyboardArrowDownIcon fontSize="small" />
            </PanButton>
            <Box />
          </Box>
        )}
      </Box>

      {/* ── Miniatures ────────────────────────────────────── */}
      {files.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            gap: '8px',
            padding: '12px 16px',
            borderTop: `1px solid ${tokens.color.border}`,
            overflowX: 'auto',
            flexShrink: 0,
            backgroundColor: tokens.color.white,
          }}
        >
          {files.map((f, idx) => (
            <Thumbnail key={`${f.url}-${idx}`} file={f} active={idx === safeIndex} onClick={() => onChange(idx)} />
          ))}
        </Box>
      )}

      {/* ── Liens nommés (DocLinksPanel) : liste verticale COLLÉE en bas du
          bloc « Document x/y » (groupes formulaire / hors-formulaire /
          non-reconnues, couleurs par type — retours François 2026-07-29).
          Hauteur plafonnée + scroll interne : détachée du bloc, elle suivait
          le scroll de la colonne de droite sur 13" (retour Luc 2026-07-30). ── */}
      <DocLinksPanel files={files} currentIndex={safeIndex} onChange={onChange} />
    </Box>
  );
};

// ─────────────────────────────────────────────────────────────

const NavButton = ({
  position,
  onClick,
  children,
}: {
  position: 'left' | 'right';
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      [position]: 12,
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(28, 28, 30, 0.7)',
      color: tokens.color.white,
      width: 40,
      height: 40,
      '&:hover': { backgroundColor: tokens.color.dark },
    }}
  >
    {children}
  </IconButton>
);

const PanButton = ({ onClick, label, children }: { onClick: () => void; label: string; children: React.ReactNode }) => (
  <Tooltip title={label} placement="top" arrow>
    <IconButton
      onClick={onClick}
      size="small"
      sx={{
        width: 30,
        height: 30,
        color: tokens.color.white,
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.22)' },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

const PdfThumbnail = ({ url, label }: { url: string; label: string }) => {
  const [dataUrl, setDataUrl] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    let alive = true;
    setDataUrl(undefined);
    void getPdfThumbnail(url).then((result) => {
      if (alive) setDataUrl(result);
    });
    return () => {
      alive = false;
    };
  }, [url]);
  if (dataUrl) {
    return <img src={dataUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top' }} />;
  }
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.color.red,
        opacity: dataUrl === undefined ? 0.5 : 1,
      }}
    >
      <PictureAsPdfIcon />
    </Box>
  );
};

const Thumbnail = ({ file, active, onClick }: { file: ViewerFile; active: boolean; onClick: () => void }) => {
  const isPdfFile = isPdf(file.url);
  return (
    <Box
      onClick={onClick}
      sx={{
        width: 64,
        height: 64,
        flexShrink: 0,
        borderRadius: `${tokens.radius.md}px`,
        border: `2px solid ${active ? tokens.color.yellow : tokens.color.border}`,
        backgroundColor: tokens.color.surface,
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        transition: 'border-color 150ms ease, transform 150ms ease',
        '&:hover': {
          borderColor: active ? tokens.color.yellow : tokens.color.borderDark,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {isPdfFile ? (
        <PdfThumbnail url={file.url} label={file.label} />
      ) : (
        <img src={file.url} alt={file.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
      {/* Tooltip via title attribut (léger). Le label complet est aussi dans le header. */}
      <Box
        title={file.label}
        sx={{
          position: 'absolute',
          inset: 0,
        }}
      />
    </Box>
  );
};
